import React from 'react';
import { Stack } from '@mui/material';

import type { IntakeStatusBaseSteps } from '@shared/patient-intake-status';

import { determineIfStepIsDisabled } from '../../utils';
import { usePatientIntakeStatusContext } from '../../hooks';
import { IdVerificationAction } from './IdVerificationAction';
import type { BaseStepsBaseProps } from './BaseSteps.types.ts';
import { MHRAccessBaseStepAction } from './MHRAccessBaseStepAction';
import { MedicareIHIBaseStepAction } from './MedicareIHIBaseStepAction';
import { HealthSummaryFormBaseStepAction } from './HealthSummaryFormBaseStepAction.tsx';
import { UploadReferralLetterOfSupportBaseStepAction } from './UploadReferralLetterOfSupportBaseStepAction.tsx';
import { ConsentToReleaseHealthSummaryBaseStepAction } from './ConsentToReleaseHealthSummaryBaseStepAction.tsx';
import { AccountVerificationAction } from './AccountVerificationAction.tsx';

const BASE_STEPS = {
  ID_VERIFICATION: IdVerificationAction,
  MEDICARE_VERIFICATION: MedicareIHIBaseStepAction,
  UPLOAD_HEALTHCARE_SUMMARY: HealthSummaryFormBaseStepAction,
  UPLOAD_REFERRAL_LETTER_OF_SUPPORT: UploadReferralLetterOfSupportBaseStepAction,
  GENERATE_CONSENT_TO_RELEASE_HEALTH_SUMMARY: ConsentToReleaseHealthSummaryBaseStepAction,
  MHR_ACCESS: MHRAccessBaseStepAction,
  ACCOUNT_VERIFICATION: AccountVerificationAction,
} satisfies Record<keyof IntakeStatusBaseSteps, React.ComponentType<BaseStepsBaseProps>>;

type BaseStepsProps = {
  onEdit: (baseField: keyof IntakeStatusBaseSteps) => void;
  showHiddenSteps: boolean;
};

export const BaseSteps: React.FC<BaseStepsProps> = ({ onEdit, showHiddenSteps }) => {
  const { baseSteps } = usePatientIntakeStatusContext();

  if (!baseSteps) {
    return null;
  }

  return (
    <Stack direction="column" gap={1} width="100%">
      {Object.entries(BASE_STEPS).map(([baseField, Component]) => {
        const isDisabled = determineIfStepIsDisabled({ step: baseSteps[baseField as keyof IntakeStatusBaseSteps] });

        if (isDisabled && !showHiddenSteps) {
          return null;
        }

        return <Component key={baseField} onEdit={() => onEdit(baseField as keyof IntakeStatusBaseSteps)} />;
      })}
    </Stack>
  );
};
